import { useRef, useState } from "react";
import { Landmark, GraduationCap, Users, CalendarHeart, Send, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { postPartenaire, uploadPartenaireLogo } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const types = [
  { id: "Partenariat financier", icone: Landmark, titre: "Partenariat financier", texte: "Soutenez durablement nos bourses, nos formations gratuites et nos cliniques d'information par une contribution financière récurrente ou ponctuelle." },
  { id: "Expertise et formation", icone: GraduationCap, titre: "Expertise et formation", texte: "Juristes, fiscalistes, comptables, stratèges : mettez votre expertise au service de nos formations, cliniques et publications." },
  { id: "Partenariat communautaire", icone: Users, titre: "Partenariat communautaire", texte: "Organismes de quartier, centres de formation, milieux d'affaires : co-organisons des activités là où les besoins se font sentir." },
  { id: "Commandite d'événement", icone: CalendarHeart, titre: "Commandite d'événement", texte: "Associez votre organisation à nos conférences, masterclasses et remises de diplômes, comme celles du programme Entrepreneurs Résilients." },
];

const inputCls =
  "w-full rounded-sm border border-input bg-background px-4 py-3.5 text-sm outline-none transition-colors duration-200 focus:border-primary focus:ring-1 focus:ring-primary";

export default function Partner() {
  const [form, setForm] = useState({ nom: "", organisation: "", courriel: "", telephone: "", type_partenariat: "Partenariat financier", message: "" });
  const [logo, setLogo] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const logoRef = useRef(null);

  const update = (champ) => (e) => setForm({ ...form, [champ]: e.target.value });

  const choisirLogo = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Format invalide", { description: "Le logo doit être une image (PNG, JPEG, WebP ou SVG)." });
      e.target.value = "";
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux", { description: "Le logo doit faire au maximum 5 Mo." });
      e.target.value = "";
      return;
    }
    setLogo(f);
  };

  const soumettre = async (e) => {
    e.preventDefault();
    if (!logo) {
      toast.error("Logo manquant", { description: "Joignez le logo de votre organisation." });
      return;
    }
    setEnvoi(true);
    try {
      const logoDoc = await uploadPartenaireLogo(logo);
      await postPartenaire({
        ...form,
        telephone: form.telephone || null,
        logo_document_id: logoDoc.document_id,
      });
      toast.success("Candidature envoyée!", {
        description: "Merci de votre intérêt. Votre candidature est en attente d'approbation : une fois approuvée, votre logo et le nom de votre organisation seront affichés dans « Nos Partenaires ».",
      });
      setForm({ nom: "", organisation: "", courriel: "", telephone: "", type_partenariat: "Partenariat financier", message: "" });
      setLogo(null);
    } catch {
      toast.error("Une erreur est survenue", {
        description: "Votre candidature n'a pas pu être envoyée. Veuillez réessayer.",
      });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <>
      <section className="bg-espresso py-28 text-ivory" data-testid="partenaire-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Devenir partenaire</p>
            <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.1] tracking-tight md:text-6xl" data-testid="partenaire-title">
              Mettez vos forces au service de <span className="italic text-primary">l'autonomie</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/70 md:text-lg">
              Seul, on va plus vite; ensemble, on va plus loin. Joignez-vous à la liste des
              partenaires de la Fondation HORA et contribuez à démocratiser l'accès à l'éducation,
              à l'expertise, aux ressources et au soutien financier.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-20 md:py-28" data-testid="partenaire-types">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Quatre façons de s'associer</p>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Choisissez votre contribution
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-border bg-border md:grid-cols-2">
            {types.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08} className="bg-background">
                <div className="h-full p-10 transition-colors duration-300 hover:bg-card" data-testid={`partenaire-type-${i}`}>
                  <t.icone size={28} strokeWidth={1.5} className="text-primary" />
                  <h3 className="mt-6 font-serif text-2xl font-semibold">{t.titre}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/60">{t.texte}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-card py-20 md:py-28" data-testid="partenaire-form-section">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold tracking-tight md:text-4xl" data-testid="partenaire-form-title">
              Souscrire à la liste des partenaires
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground/60 md:text-base">
              Remplissez ce formulaire et un membre de notre équipe vous contactera pour discuter
              du partenariat qui vous convient.
            </p>

            <form onSubmit={soumettre} className="mt-10 space-y-6" data-testid="partenaire-form">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="p-org" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Nom de l'organisation *</label>
                  <input id="p-org" required minLength={2} value={form.organisation} onChange={update("organisation")} className={inputCls} placeholder="Nom de votre organisation" data-testid="partenaire-organisation-input" />
                </div>
                <div>
                  <label htmlFor="p-nom" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Nom du contact *</label>
                  <input id="p-nom" required minLength={2} value={form.nom} onChange={update("nom")} className={inputCls} placeholder="Personne à contacter" data-testid="partenaire-nom-input" />
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="p-courriel" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Courriel *</label>
                  <input id="p-courriel" type="email" required value={form.courriel} onChange={update("courriel")} className={inputCls} placeholder="vous@exemple.ca" data-testid="partenaire-courriel-input" />
                </div>
                <div>
                  <label htmlFor="p-tel" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Téléphone</label>
                  <input id="p-tel" type="tel" value={form.telephone} onChange={update("telephone")} className={inputCls} placeholder="514 000-0000" data-testid="partenaire-telephone-input" />
                </div>
              </div>
              <div>
                <label htmlFor="p-type" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Type de partenariat *</label>
                <select id="p-type" value={form.type_partenariat} onChange={update("type_partenariat")} className={inputCls} data-testid="partenaire-type-select">
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.titre}</option>
                  ))}
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label htmlFor="p-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Description courte du partenariat souhaité *</label>
                <textarea id="p-message" required minLength={10} rows={5} value={form.message} onChange={update("message")} className={`${inputCls} resize-y`} placeholder="Décrivez brièvement le partenariat que vous souhaitez…" data-testid="partenaire-message-textarea" />
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Logo de l'organisation *</p>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  ref={logoRef}
                  onChange={choisirLogo}
                  data-testid="partenaire-logo-input"
                />
                <button
                  type="button"
                  onClick={() => logoRef.current?.click()}
                  data-testid="partenaire-logo-button"
                  className={`flex w-full items-center gap-3 rounded-sm border px-4 py-3.5 text-sm transition-colors duration-200 ${
                    logo
                      ? "border-primary/50 bg-primary/5 text-foreground"
                      : "border-dashed border-border bg-background text-foreground/50 hover:border-primary hover:text-primary"
                  }`}
                >
                  <ImagePlus size={16} className={logo ? "text-primary" : ""} />
                  <span className="truncate">{logo ? logo.name : "PNG, JPEG, WebP ou SVG — 5 Mo maximum"}</span>
                </button>
                <p className="mt-2 text-xs leading-relaxed text-foreground/50">
                  Une fois votre candidature approuvée par notre équipe, ce logo et le nom de votre organisation seront affichés dans la section « Nos Partenaires » du site.
                </p>
              </div>
              <button
                type="submit"
                disabled={envoi}
                data-testid="partenaire-submit-button"
                className="group flex items-center gap-3 rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,transform,opacity] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {envoi ? "Envoi en cours…" : "Rejoindre la liste des partenaires"}
                <Send size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <p className="text-xs leading-relaxed text-foreground/50" data-testid="partenaire-consent-note">
                En envoyant ce formulaire, vous consentez à ce que vos renseignements soient
                utilisés pour donner suite à votre demande, conformément à notre politique de
                protection des renseignements personnels (lien dans le pied de page).
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
